import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  Avatar,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Menu as MenuIcon,
  DarkMode,
  LightMode,
  Language,
} from "@mui/icons-material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { OrganizationSwitcher } from "@/components/organization/OrganizationSwitcher";
import { useThemeMode } from "@/theme/ThemeContext";
import { useAppSelector, useAppDispatch } from "@/store";
import { logout } from "@/store/slices/authSlice";

interface TopBarProps {
  onMenuToggle: () => void;
}

export function TopBar({ onMenuToggle }: TopBarProps) {
  const { t, i18n } = useTranslation();
  const { mode, toggleTheme } = useThemeMode();
  const user = useAppSelector((s) => s.auth.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleLanguageToggle = () => {
    i18n.changeLanguage(i18n.language === "en" ? "hr" : "en");
  };

  const handleLogout = async () => {
    setAnchorEl(null);
    await dispatch(logout());
    navigate("/");
  };

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        width: "100%",
        color: "text.primary",
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        backdropFilter: "blur(8px)",
        backgroundColor: (theme) =>
          theme.palette.mode === "light"
            ? "rgba(255,255,255,0.88)"
            : "rgba(30,41,59,0.92)",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          px: { xs: 1, sm: 2 },
          minHeight: { xs: 56, sm: 64 },
          gap: 1,
        }}
      >
        <IconButton
          edge="start"
          onClick={onMenuToggle}
          aria-label="Open menu"
          sx={{
            mr: { xs: 0, sm: 0.5 },
            display: { md: "none" },
            color: "text.secondary",
          }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            minWidth: 0,
            flex: "1 1 0",
          }}
        >
          <OrganizationSwitcher />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={toggleTheme}
            size="small"
            sx={{ color: "text.secondary" }}
            aria-label={
              mode === "light" ? "Switch to dark mode" : "Switch to light mode"
            }
          >
            {mode === "light" ? <DarkMode /> : <LightMode />}
          </IconButton>
          <IconButton
            onClick={handleLanguageToggle}
            size="small"
            sx={{ color: "text.secondary" }}
            aria-label="Change language"
          >
            <Language />
          </IconButton>

          {user && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 0.75, my: 1, borderColor: "divider" }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  cursor: "pointer",
                  pl: 0.25,
                }}
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                <Box
                  sx={{
                    textAlign: "right",
                    display: { xs: "none", sm: "block" },
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: "0.8125rem",
                      lineHeight: 1.3,
                    }}
                  >
                    {user.username || user.email}
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: "0.875rem",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  {(user.username || user.email)[0].toUpperCase()}
                </Avatar>
              </Box>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    navigate("/account");
                  }}
                >
                  {t("account.title")}
                </MenuItem>
                <MenuItem onClick={handleLogout}>{t("auth.signOut")}</MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}
